<template>
  <div>
    <admin-nav active="formules" />
    <b-container fluid>
      <b-row class="mb-1">
        <b-col>
          <h3>Formules de domiciliation</h3>
          <small class="text-muted">
            Catalogue des packs proposés au client, par type de profil
            (entreprise / association / particulier). Modifications visibles
            immédiatement côté parcours public.
          </small>
        </b-col>
        <b-col class="text-right">
          <b-button
            variant="primary"
            @click="ouvrirNouvelle"
          >
            <feather-icon icon="PlusIcon" /> Nouvelle formule
          </b-button>
        </b-col>
      </b-row>

      <div
        v-for="groupe in formulesGroupees"
        :key="groupe.typeClient"
        class="mb-2"
      >
        <h5 class="text-capitalize text-primary">
          {{ libelleType(groupe.typeClient) }}
        </h5>
        <b-card
          v-for="f in groupe.formules"
          :key="f.UI"
          class="mb-1"
          :border-variant="f.actif ? 'light' : 'secondary'"
        >
          <b-row>
            <b-col md="2">
              <strong>{{ f.nom }}</strong>
              <div class="text-muted small">{{ f.pack }}</div>
              <div
                v-if="f.benefice"
                class="text-muted small font-italic"
              >
                {{ f.benefice }}
              </div>
              <b-badge
                v-if="!f.actif"
                variant="secondary"
                class="mt-50"
              >
                Inactive
              </b-badge>
              <b-badge
                v-if="f.badge"
                variant="success"
                class="mt-50"
              >
                {{ f.badge }}
              </b-badge>
              <b-badge
                v-if="f.badgeAnnuel"
                variant="danger"
                class="mt-50"
                title="Visible uniquement en facturation annuelle"
              >
                {{ f.badgeAnnuel }}
              </b-badge>
            </b-col>
            <b-col md="2">
              <div class="text-muted small">Mensuel HT</div>
              <strong>{{ f.prixMensuelHT.toFixed(2) }} €</strong>
            </b-col>
            <b-col md="2">
              <div class="text-muted small">Annuel HT</div>
              <strong>{{ f.prixAnnuelHT.toFixed(2) }} €</strong>
            </b-col>
            <b-col md="3">
              <div class="text-muted small mb-50">{{ f.features.length }} feature(s)</div>
              <div
                v-for="x in f.features"
                :key="x.id"
                class="small"
              >
                <feather-icon
                  :icon="x.inclus ? 'CheckCircleIcon' : 'XCircleIcon'"
                  :class="x.inclus ? 'text-success' : 'text-muted'"
                  size="14"
                />
                {{ x.libelle }}
              </div>
            </b-col>
            <b-col
              md="3"
              class="text-right"
            >
              <b-button
                size="sm"
                variant="outline-primary"
                class="mr-1"
                @click="ouvrirEdition(f)"
              >
                <feather-icon icon="EditIcon" /> Éditer
              </b-button>
              <b-button
                size="sm"
                variant="outline-secondary"
                class="mr-1"
                @click="ouvrirFeatures(f)"
              >
                <feather-icon icon="ListIcon" /> Features
              </b-button>
              <b-button
                size="sm"
                :variant="f.actif ? 'outline-danger' : 'outline-success'"
                @click="toggleActif(f)"
              >
                {{ f.actif ? 'Désactiver' : 'Activer' }}
              </b-button>
            </b-col>
          </b-row>
        </b-card>
      </div>

      <!-- Édition / création formule -->
      <b-modal
        v-model="showEdit"
        :title="edit.UI ? 'Modifier la formule' : 'Nouvelle formule'"
        size="lg"
        ok-title="Enregistrer"
        cancel-title="Annuler"
        @ok="enregistrer"
      >
        <b-row>
          <b-col md="6">
            <b-form-group label="Type de client *">
              <b-form-select
                v-model="edit.typeClient"
                :options="optionsTypeClient"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Nom (interne) *">
              <b-form-input
                v-model="edit.nom"
                placeholder="PREMIUM"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Pack (affiché) *">
              <b-form-input
                v-model="edit.pack"
                placeholder="Full Pack"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Bénéfice / accroche (optionnel)">
              <b-form-input
                v-model="edit.benefice"
                placeholder="Recevez votre courrier où que vous soyez"
              />
              <small class="text-muted">
                Phrase affichée sous le nom du pack dans le parcours. Ce champ fait
                autorité : laissé vide, aucune accroche n'est affichée.
              </small>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Badge (optionnel)">
              <b-form-input
                v-model="edit.badge"
                placeholder="Le + complet"
              />
              <small class="text-muted">Affiché quel que soit le mode de facturation.</small>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Badge promo annuel (optionnel)">
              <b-form-input
                v-model="edit.badgeAnnuel"
                placeholder="1 mois offert"
              />
              <small class="text-muted">
                Affiché sur la carte du pack <strong>uniquement</strong> quand le
                client choisit la facturation annuelle. Laisser vide pour ne rien afficher.
              </small>
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Prix mensuel HT (€)">
              <b-form-input
                v-model.number="edit.prixMensuelHT"
                type="number"
                step="0.01"
              />
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Prix annuel HT (€)">
              <b-form-input
                v-model.number="edit.prixAnnuelHT"
                type="number"
                step="0.01"
              />
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Ordre d'affichage">
              <b-form-input
                v-model.number="edit.ordre"
                type="number"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </b-modal>

      <!-- Édition features -->
      <b-modal
        v-model="showFeatures"
        size="lg"
        :title="`Features — ${editFeatures.nom || ''}`"
        ok-only
        ok-title="Fermer"
      >
        <b-table
          v-if="editFeatures.features.length"
          :items="editFeatures.features"
          :fields="featureFields"
          striped
        >
          <template #cell(libelle)="row">
            <b-form-input
              v-model="row.item.libelle"
              size="sm"
            />
          </template>
          <template #cell(code)="row">
            <b-form-input
              v-model="row.item.code"
              size="sm"
            />
          </template>
          <template #cell(inclus)="row">
            <b-form-checkbox v-model="row.item.inclus" />
          </template>
          <template #cell(ordre)="row">
            <b-form-input
              v-model.number="row.item.ordre"
              type="number"
              size="sm"
              style="width: 70px"
            />
          </template>
          <template #cell(actions)="row">
            <b-button
              size="sm"
              variant="primary"
              class="mr-50"
              @click="enregistrerFeature(row.item)"
            >
              Enregistrer
            </b-button>
            <b-button
              size="sm"
              variant="outline-danger"
              @click="supprimerFeature(row.item)"
            >
              <feather-icon icon="TrashIcon" />
            </b-button>
          </template>
        </b-table>
        <p
          v-else
          class="text-muted"
        >
          Aucune feature pour le moment.
        </p>
        <hr>
        <h6>Ajouter une feature</h6>
        <b-row>
          <b-col md="3">
            <b-form-input
              v-model="newFeature.code"
              placeholder="Code (ex. domiciliation)"
              size="sm"
            />
          </b-col>
          <b-col md="5">
            <b-form-input
              v-model="newFeature.libelle"
              placeholder="Libellé affiché"
              size="sm"
            />
          </b-col>
          <b-col md="1">
            <b-form-checkbox v-model="newFeature.inclus">
              Inclus
            </b-form-checkbox>
          </b-col>
          <b-col md="1">
            <b-form-input
              v-model.number="newFeature.ordre"
              type="number"
              placeholder="100"
              size="sm"
            />
          </b-col>
          <b-col md="2">
            <b-button
              variant="primary"
              size="sm"
              @click="ajouterFeature"
            >
              Ajouter
            </b-button>
          </b-col>
        </b-row>
      </b-modal>
    </b-container>
  </div>
</template>

<script>
import {
  BContainer,
  BRow,
  BCol,
  BCard,
  BButton,
  BBadge,
  BModal,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BFormCheckbox,
  BTable,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import AdminNav from './AdminNav.vue'

export default {
  components: {
    BContainer,
    BRow,
    BCol,
    BCard,
    BButton,
    BBadge,
    BModal,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BFormCheckbox,
    BTable,
    AdminNav,
  },
  data() {
    return {
      formules: [],
      showEdit: false,
      edit: {},
      showFeatures: false,
      editFeatures: { nom: '', features: [], UI: '' },
      newFeature: {
        code: '',
        libelle: '',
        inclus: true,
        ordre: 100,
      },
      featureFields: [
        { key: 'code', label: 'Code' },
        { key: 'libelle', label: 'Libellé' },
        { key: 'inclus', label: 'Inclus' },
        { key: 'ordre', label: 'Ordre' },
        { key: 'actions', label: '' },
      ],
      optionsTypeClient: [
        { value: 'entreprise', text: 'Entreprise' },
        { value: 'association', text: 'Association' },
        { value: 'particulier', text: 'Particulier' },
      ],
    }
  },
  computed: {
    formulesGroupees() {
      const buckets = { entreprise: [], association: [], particulier: [] }
      this.formules.forEach(f => {
        const t = f.typeClient || 'entreprise'
        if (!buckets[t]) buckets[t] = []
        buckets[t].push(f)
      })
      return Object.keys(buckets)
        .map(k => ({ typeClient: k, formules: buckets[k] }))
        .filter(g => g.formules.length)
    },
  },
  created() {
    this.charger()
  },
  methods: {
    authHeaders() {
      return { headers: { 'x-access-token': localStorage.getItem('domAdminToken') } }
    },
    libelleType(t) {
      return ({ entreprise: 'Entreprise', association: 'Association', particulier: 'Particulier' }[t] || t)
    },
    charger() {
      this.$http.get('/admindom/formules', this.authHeaders())
        .then(response => {
          if (response.data.res === 1) this.formules = response.data.formules
          else this.toast('Erreur de chargement', 'danger', 'AlertTriangleIcon')
        })
    },
    ouvrirNouvelle() {
      this.edit = {
        UI: null,
        typeClient: 'entreprise',
        nom: '',
        pack: '',
        prixMensuelHT: 0,
        prixAnnuelHT: 0,
        benefice: '',
        badge: '',
        badgeAnnuel: '',
        ordre: 100,
      }
      this.showEdit = true
    },
    ouvrirEdition(f) {
      this.edit = { ...f }
      this.showEdit = true
    },
    enregistrer() {
      const payload = {
        typeClient: this.edit.typeClient,
        nom: this.edit.nom,
        pack: this.edit.pack,
        prixMensuelHT: this.edit.prixMensuelHT,
        prixAnnuelHT: this.edit.prixAnnuelHT,
        benefice: this.edit.benefice || '',
        badge: this.edit.badge,
        badgeAnnuel: this.edit.badgeAnnuel || '',
        ordre: this.edit.ordre,
      }
      const req = this.edit.UI
        ? this.$http.patch(`/admindom/formules/${this.edit.UI}`, payload, this.authHeaders())
        : this.$http.post('/admindom/formules', payload, this.authHeaders())
      req.then(response => {
        if (response.data.res === 1) {
          this.toast('Formule enregistrée', 'success', 'CheckIcon')
          this.charger()
        } else {
          this.toast(response.data.message || 'Erreur', 'danger', 'AlertTriangleIcon')
        }
      })
    },
    toggleActif(f) {
      this.$http.patch(`/admindom/formules/${f.UI}`, { actif: !f.actif }, this.authHeaders())
        .then(response => {
          if (response.data.res === 1) {
            this.toast(f.actif ? 'Formule désactivée' : 'Formule activée', 'success', 'CheckIcon')
            this.charger()
          }
        })
    },
    ouvrirFeatures(f) {
      this.editFeatures = {
        UI: f.UI,
        nom: f.nom,
        features: f.features.map(x => ({ ...x })),
      }
      this.newFeature = {
        code: '',
        libelle: '',
        inclus: true,
        ordre: 100,
      }
      this.showFeatures = true
    },
    enregistrerFeature(item) {
      const payload = {
        code: item.code,
        libelle: item.libelle,
        inclus: item.inclus,
        ordre: item.ordre,
      }
      this.$http.patch(`/admindom/formules/${this.editFeatures.UI}/features/${item.id}`, payload, this.authHeaders())
        .then(response => {
          if (response.data.res === 1) {
            this.toast('Feature mise à jour', 'success', 'CheckIcon')
            this.charger()
          }
        })
    },
    supprimerFeature(item) {
      this.$http.delete(`/admindom/formules/${this.editFeatures.UI}/features/${item.id}`, this.authHeaders())
        .then(response => {
          if (response.data.res === 1) {
            this.editFeatures.features = this.editFeatures.features.filter(x => x.id !== item.id)
            this.charger()
          }
        })
    },
    ajouterFeature() {
      if (!this.newFeature.libelle) {
        this.toast('Libellé requis', 'warning', 'AlertTriangleIcon')
        return
      }
      this.$http.post(`/admindom/formules/${this.editFeatures.UI}/features`, this.newFeature, this.authHeaders())
        .then(response => {
          if (response.data.res === 1) {
            this.editFeatures.features.push({
              id: response.data.id,
              code: this.newFeature.code,
              libelle: this.newFeature.libelle,
              inclus: this.newFeature.inclus,
              ordre: this.newFeature.ordre,
            })
            this.newFeature = {
              code: '',
              libelle: '',
              inclus: true,
              ordre: 100,
            }
            this.charger()
          }
        })
    },
    toast(title, variant, icon) {
      this.$toast({
        component: ToastificationContent,
        position: 'top-right',
        props: { title, variant, icon },
      })
    },
  },
}
</script>
